import * as React from "react";

import type { MediaDockKind } from "@/components/OmniCine/Docks/shared/mediaDock.types";

type MediaPanelPlaybackMode = "closed" | "ingest" | "focus" | "overlay";

type UseDockPlaybackStateArgs = {
  panelMode: MediaPanelPlaybackMode;
  previewKind: MediaDockKind;
  previewUrl: string | null;
};

type UseDockPlaybackStateResult = {
  previewVideoRef: React.RefObject<HTMLVideoElement | null>;
  previewAudioRef: React.RefObject<HTMLAudioElement | null>;
  mediaDurationSec: number;
  mediaCurrentTimeSec: number;
  mediaIsPlaying: boolean;
  mediaMuted: boolean;
  syncActiveMediaState: () => void;
  onGlassPlayPause: () => void;
  onGlassMuteToggle: () => void;
  onGlassSeek: (nextSec: number) => void;
};

type MediaPlaybackSnapshot = {
  mediaDurationSec: number;
  mediaCurrentTimeSec: number;
  mediaIsPlaying: boolean;
  mediaMuted: boolean;
};

const EMPTY_MEDIA_PLAYBACK_SNAPSHOT: MediaPlaybackSnapshot = {
  mediaDurationSec: 0,
  mediaCurrentTimeSec: 0,
  mediaIsPlaying: false,
  mediaMuted: false,
};

function snapshotsMatch(a: MediaPlaybackSnapshot, b: MediaPlaybackSnapshot): boolean {
  return (
    a.mediaDurationSec === b.mediaDurationSec &&
    Math.abs(a.mediaCurrentTimeSec - b.mediaCurrentTimeSec) < 0.05 &&
    a.mediaIsPlaying === b.mediaIsPlaying &&
    a.mediaMuted === b.mediaMuted
  );
}

export default function useDockPlaybackState({
  panelMode,
  previewKind,
  previewUrl,
}: UseDockPlaybackStateArgs): UseDockPlaybackStateResult {
  const [playbackSnapshot, setPlaybackSnapshot] = React.useState<MediaPlaybackSnapshot>(
    EMPTY_MEDIA_PLAYBACK_SNAPSHOT,
  );
  const previewVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const previewAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const playbackSnapshotRef = React.useRef<MediaPlaybackSnapshot>(EMPTY_MEDIA_PLAYBACK_SNAPSHOT);
  const syncRafRef = React.useRef<number | null>(null);

  const getActiveMediaEl = React.useCallback((): HTMLMediaElement | null => {
    if (panelMode !== "focus" && panelMode !== "overlay") return null;
    if (previewKind === "video") return previewVideoRef.current;
    if (previewKind === "audio") return previewAudioRef.current;
    return null;
  }, [panelMode, previewKind]);

  const commitPlaybackSnapshot = React.useCallback((nextSnapshot: MediaPlaybackSnapshot) => {
    const normalizedSnapshot: MediaPlaybackSnapshot = {
      mediaDurationSec:
        Number.isFinite(nextSnapshot.mediaDurationSec) && nextSnapshot.mediaDurationSec > 0
          ? nextSnapshot.mediaDurationSec
          : 0,
      mediaCurrentTimeSec:
        Number.isFinite(nextSnapshot.mediaCurrentTimeSec) && nextSnapshot.mediaCurrentTimeSec > 0
          ? nextSnapshot.mediaCurrentTimeSec
          : 0,
      mediaIsPlaying: !!nextSnapshot.mediaIsPlaying,
      mediaMuted: !!nextSnapshot.mediaMuted,
    };

    if (snapshotsMatch(playbackSnapshotRef.current, normalizedSnapshot)) {
      return;
    }

    playbackSnapshotRef.current = normalizedSnapshot;
    setPlaybackSnapshot(normalizedSnapshot);
  }, []);

  const syncActiveMediaStateNow = React.useCallback(() => {
    const el = getActiveMediaEl();
    if (!el) {
      commitPlaybackSnapshot(EMPTY_MEDIA_PLAYBACK_SNAPSHOT);
      return;
    }

    commitPlaybackSnapshot({
      mediaDurationSec: Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0,
      mediaCurrentTimeSec: Number.isFinite(el.currentTime) ? el.currentTime : 0,
      mediaIsPlaying: !el.paused && !el.ended,
      mediaMuted: !!el.muted,
    });
  }, [commitPlaybackSnapshot, getActiveMediaEl]);

  const syncActiveMediaState = React.useCallback(() => {
    if (typeof window === "undefined") {
      syncActiveMediaStateNow();
      return;
    }

    if (syncRafRef.current != null) {
      return;
    }

    syncRafRef.current = window.requestAnimationFrame(() => {
      syncRafRef.current = null;
      syncActiveMediaStateNow();
    });
  }, [syncActiveMediaStateNow]);

  React.useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && syncRafRef.current != null) {
        window.cancelAnimationFrame(syncRafRef.current);
        syncRafRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (panelMode !== "focus" && panelMode !== "overlay") {
      commitPlaybackSnapshot(EMPTY_MEDIA_PLAYBACK_SNAPSHOT);
      return;
    }

    if ((previewKind !== "audio" && previewKind !== "video") || !previewUrl) {
      commitPlaybackSnapshot(EMPTY_MEDIA_PLAYBACK_SNAPSHOT);
      return;
    }

    syncActiveMediaStateNow();
  }, [commitPlaybackSnapshot, panelMode, previewKind, previewUrl, syncActiveMediaStateNow]);

  React.useEffect(() => {
    if (panelMode !== "focus" && panelMode !== "overlay") return;
    if (previewKind !== "audio") return;
    if (!previewUrl || typeof Audio === "undefined") return;

    let alive = true;
    const probe = new Audio();
    probe.preload = "metadata";

    const onLoadedMetadata = () => {
      if (!alive) return;
      const nextDuration = Number.isFinite(probe.duration) && probe.duration > 0 ? probe.duration : 0;
      if (nextDuration > 0) {
        commitPlaybackSnapshot({
          ...playbackSnapshotRef.current,
          mediaDurationSec: nextDuration,
        });
      }
    };

    probe.addEventListener("loadedmetadata", onLoadedMetadata);
    probe.src = previewUrl;
    probe.load();

    return () => {
      alive = false;
      probe.removeEventListener("loadedmetadata", onLoadedMetadata);
      probe.src = "";
    };
  }, [commitPlaybackSnapshot, panelMode, previewKind, previewUrl]);

  const onGlassPlayPause = React.useCallback(() => {
    const el = getActiveMediaEl();
    if (!el) return;

    try {
      if (el.paused || el.ended) {
        const maybe = el.play();
        if (maybe && typeof (maybe as Promise<void>).catch === "function") {
          (maybe as Promise<void>).catch(() => {
            // ignore
          });
        }
      } else {
        el.pause();
      }
      syncActiveMediaState();
    } catch {
      // ignore
    }
  }, [getActiveMediaEl, syncActiveMediaState]);

  const onGlassMuteToggle = React.useCallback(() => {
    const el = getActiveMediaEl();
    if (!el) return;
    el.muted = !el.muted;
    syncActiveMediaState();
  }, [getActiveMediaEl, syncActiveMediaState]);

  const onGlassSeek = React.useCallback(
    (nextSec: number) => {
      const el = getActiveMediaEl();
      if (!el) return;
      const safeDuration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : nextSec;
      const clamped = Math.max(0, Math.min(nextSec, safeDuration));
      el.currentTime = clamped;
      syncActiveMediaState();
    },
    [getActiveMediaEl, syncActiveMediaState],
  );

  return {
    previewVideoRef,
    previewAudioRef,
    mediaDurationSec: playbackSnapshot.mediaDurationSec,
    mediaCurrentTimeSec: playbackSnapshot.mediaCurrentTimeSec,
    mediaIsPlaying: playbackSnapshot.mediaIsPlaying,
    mediaMuted: playbackSnapshot.mediaMuted,
    syncActiveMediaState,
    onGlassPlayPause,
    onGlassMuteToggle,
    onGlassSeek,
  };
}